import React, { useState } from 'react';
import QuoteDetailModal from '../Quote/QuoteDetailModal';
import { logInfo } from '../../config_and_helpers/logger';
import './AiChatStyle.css';

const LOG_CONTEXT = 'AiChatQuoteCitationsTable';

/**
 * @param {Object} citation
 * @returns {string}
 */
const getQuoteIdLabel = citation =>
  citation?.quote_id != null ? `Quote ${citation.quote_id}` : '—';

/**
 * @param {Object} citation
 * @returns {string}
 */
const getQuoteName = citation => citation?.name?.trim() || '—';

/**
 * Tabular list of quote citations with Quote ID and Name columns.
 *
 * @param {Object} props
 * @param {Array<Object>} props.citations
 * @returns {JSX.Element|null}
 */
const AiChatQuoteCitationsTable = ({ citations = [] }) => {
  const [openQuoteId, setOpenQuoteId] = useState(null);

  const quoteCitations = citations.filter(
    citation => citation?.entity_type === 'quote' && citation?.quote_id
  );

  if (quoteCitations.length === 0) return null;

  const openCitation = quoteCitations.find(
    citation => String(citation.quote_id) === String(openQuoteId)
  );

  const handleRowActivate = citation => {
    logInfo(LOG_CONTEXT, 'opening quote citation', {
      quote_id: citation.quote_id,
      name: citation.name,
    });
    setOpenQuoteId(citation.quote_id);
  };

  return (
    <>
      <table className="ai-chat-citations-table">
        <thead>
          <tr>
            <th scope="col">Quote</th>
            <th scope="col">Name</th>
          </tr>
        </thead>
        <tbody>
          {quoteCitations.map((citation, idx) => {
            const idLabel = getQuoteIdLabel(citation);
            const name = getQuoteName(citation);
            const isOpen =
              openQuoteId != null &&
              String(openQuoteId) === String(citation.quote_id);

            return (
              <tr
                key={`quote-${citation.quote_id}-${idx}`}
                className={
                  isOpen
                    ? 'ai-chat-citations-table__row ai-chat-citations-table__row--active'
                    : 'ai-chat-citations-table__row'
                }
                tabIndex={0}
                role="button"
                aria-label={`Open ${idLabel} ${name}`}
                onClick={() => handleRowActivate(citation)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowActivate(citation);
                  }
                }}
              >
                <td className="ai-chat-citations-table__primary" title={idLabel}>
                  {idLabel}
                </td>
                <td className="ai-chat-citations-table__secondary" title={name}>
                  {name}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {openCitation && (
        <div className="ai-chat-citations-table__modal-host" aria-hidden="true">
          <QuoteDetailModal
            key={openCitation.quote_id}
            quoteId={openCitation.quote_id}
            hideTrigger
            show
            onHide={() => {
              logInfo(LOG_CONTEXT, 'closing quote citation modal', {
                quote_id: openCitation.quote_id,
              });
              setOpenQuoteId(null);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AiChatQuoteCitationsTable;
